import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, CheckCircle2, CircleDollarSign, Package, Store, TicketCheck, Users } from "lucide-react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { EmptyState, PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

export function OverviewPage() {
  const apiBase = getAPIBaseURL();

  // Fetch all dashboard data from API
  const { data: dashboardData } = useQuery({
    queryKey: ["dashboard-all"],
    queryFn: async () => {
      try {
        const response = await fetch(`${apiBase}/api/v1/admin/stats`, {
          headers: getAdminHeaders(),
        });
        if (!response.ok) throw new Error("Failed to fetch dashboard data");
        return await response.json();
      } catch (error) {
        return { monthly: [], daily: [], statistics: {} };
      }
    },
  });

  const statsData = dashboardData?.statistics || {};
  const summaryData = dashboardData?.statistics || {};
  const approvalsData = dashboardData?.statistics || {};

  // Process analytics data for charts
  const monthlyData = (dashboardData?.monthly || []).map((item: any) => ({
    month: new Date(`${item.month}-01T00:00:00`).toLocaleDateString("en-US", { month: "short" }),
    users: item.new_users || 0,
  }));

  const dailyData = (dashboardData?.daily || []).map((item: any) => ({
    day: new Date(`${item.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    orders: item.orders || 0,
  }));

  // Build KPIs from real data
  const kpis = [
    {
      label: "Total users",
      value: String(statsData?.total_users || 0),
      note: `${statsData?.total_users || 0} registered users`,
      change: `${statsData?.change?.users ?? 0}%`,
      icon: Users,
    },
    {
      label: "Orders of the month",
      value: String(summaryData?.total_orders_month || 0),
      note: `${summaryData?.total_orders_month || 0} orders this month`,
      change: `${statsData?.change?.orders ?? 0}%`,
      icon: Package,
    },
    {
      label: "USD commission",
      value: `US$${Number(summaryData?.revenue_month || 0).toLocaleString()}`,
      note: `$${Number(summaryData?.revenue_month || 0).toLocaleString()} this month`,
      change: `${statsData?.change?.commission ?? 0}%`,
      icon: CircleDollarSign,
    },
    {
      label: "Active shops",
      value: String(statsData?.active_shops || 0),
      note: `${statsData?.total_shops || 0} total shops`,
      change: `${statsData?.change?.active_shops ?? 0}%`,
      icon: Store,
    },
  ];

  const priorities = [
    {
      title: "Ticket support",
      note: "Open the relevant queue",
      count: statsData?.open_tickets || 0,
      icon: TicketCheck,
    },
    {
      title: "KYC checks",
      note: "Review seller documents",
      count: approvalsData?.pending_kyc || 0,
      icon: CheckCircle2,
    },
    {
      title: "Products to validate",
      note: "Approve catalogue items",
      count: approvalsData?.pending_products || 0,
      icon: Package,
    },
    {
      title: "Shops on hold",
      note: "Resolve policy issues",
      count: approvalsData?.pending_sellers || 0,
      icon: Store,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Overview" description="Growth, revenue and operations in real time" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map(({ label, value, note, change, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
                  <p className="mt-3 text-2xl font-bold tabular-nums">{value}</p>
                </div>
                <div className="grid size-10 place-items-center rounded-xl bg-muted">
                  <Icon className="size-4" />
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between border-t pt-3 text-xs">
                <span className="text-muted-foreground">{note}</span>
                <span className="inline-flex items-center font-semibold text-success">
                  <ArrowUpRight className="size-3" />
                  {change}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-[3fr_2fr]">
        <ChartCard title="User growth" subtitle="New accounts per month in 2026">
          <ChartContainer config={{ users: { label: "New users", color: "var(--primary)" } }} className="h-64 w-full aspect-auto">
            <LineChart data={monthlyData} margin={{ left: -20, right: 12, top: 12 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="month" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="users" stroke="var(--color-users)" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ChartContainer>
        </ChartCard>
        <ChartCard title="Daily activity" subtitle="Orders placed within the last 14 days">
          <ChartContainer config={{ orders: { label: "Orders", color: "var(--chart-2)" } }} className="h-64 w-full aspect-auto">
            <LineChart data={dailyData} margin={{ left: -24, right: 10, top: 12 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="day" tickLine={false} axisLine={false} minTickGap={18} />
              <YAxis tickLine={false} axisLine={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="orders" stroke="var(--color-orders)" strokeWidth={2} dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>
      </div>
      <div className="grid gap-4 xl:grid-cols-[4fr_5fr]">
        <Card>
          <CardHeader className="border-b p-5">
            <h3 className="font-semibold">Deuxda Ecosystem</h3>
            <p className="text-sm text-muted-foreground">Breakdown of the main active volumes.</p>
          </CardHeader>
          <CardContent className="p-0">
            <EmptyState title="No data available" message="Ecosystem data will appear after the next synchronization." />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="border-b p-5">
            <h3 className="font-semibold">Operational priorities</h3>
            <p className="text-sm text-muted-foreground">The cases that require intervention now.</p>
          </CardHeader>
          <CardContent className="grid gap-3 p-5 sm:grid-cols-2">
            {priorities.map(({ title, note, count, icon: Icon }) => (
              <button
                key={title}
                className="grid min-h-20 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-border/60 bg-muted/25 p-3 text-left transition-colors hover:border-primary/25 hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="grid size-9 place-items-center rounded-xl bg-card text-primary shadow-sm">
                  <Icon className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{title}</span>
                  <span className="block truncate text-xs text-muted-foreground">{note}</span>
                </span>
                <span className="text-xl font-bold tabular-nums">{count}</span>
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b p-4">
        <h3 className="font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent className="p-4">{children}</CardContent>
    </Card>
  );
}
